/**
 * SMS Guards
 *
 * Guards for SMS actor result handling.
 * Pattern: Wrapper object with metadata + implementation.
 */

import type { Result } from "neverthrow";

/**
 * Guard function signature
 */
export type GuardFunction = (args: {
  context: Record<string, unknown>;
  event: {
    type: string;
    output?: unknown;
    data?: unknown;
    error?: unknown;
  };
}) => boolean;

/**
 * Guard metadata
 */
export interface GuardMetadata {
  displayName: string;
  description: string;
  category: "success" | "error" | "context" | "validation";
  requiresActor?: string;
  requiresContext?: readonly string[];
}

/**
 * Guard with metadata wrapper
 */
export interface GuardWithMetadata {
  impl: GuardFunction;
  metadata: GuardMetadata;
}

/**
 * Type-safe result checker
 */
function isResult<T, E>(result: unknown): result is Result<T, E> {
  return (
    result !== null &&
    typeof result === "object" &&
    "isOk" in result &&
    typeof result.isOk === "function"
  );
}

/**
 * SMS Success Guard
 * Checks if SMS was sent successfully
 */
export const smsSuccess: GuardWithMetadata = {
  impl: ({ event }) => {
    // Check if it's a Result type (old neverthrow pattern)
    if (event.output && isResult(event.output)) {
      return event.output.isOk();
    }
    // For new pattern: success means no error
    return !event.error;
  },
  metadata: {
    displayName: "smsSuccess",
    description: "SMS was sent successfully",
    requiresActor: "sendSMS",
    category: "success",
  },
};

/**
 * SMS Error Guard
 * Checks if SMS failed
 */
export const smsError: GuardWithMetadata = {
  impl: ({ event }) => {
    // Check if it's a Result type (old neverthrow pattern)
    if (event.output && isResult(event.output)) {
      return event.output.isErr();
    }
    // For new pattern: error exists
    return !!event.error;
  },
  metadata: {
    displayName: "smsError",
    description: "SMS failed to send",
    requiresActor: "sendSMS",
    category: "error",
  },
};

/**
 * SMS Retryable Guard
 * Checks if SMS error is retryable
 */
export const smsRetryable: GuardWithMetadata = {
  impl: ({ event }) => {
    // Check if it's a Result type (old neverthrow pattern)
    if (event.output && isResult(event.output)) {
      if (event.output.isOk()) return false;
      const error = event.output.error as { retryable?: boolean };
      return error.retryable === true;
    }

    // For new pattern: check event.error
    if (!event.error || typeof event.error !== "object") return false;
    const error = event.error as { retryable?: boolean };
    return error.retryable === true;
  },
  metadata: {
    displayName: "smsRetryable",
    description: "SMS error can be retried",
    requiresActor: "sendSMS",
    category: "error",
  },
};

/**
 * SMS Rate Limited Guard
 * Checks if SMS was rate limited
 */
export const smsRateLimited: GuardWithMetadata = {
  impl: ({ event }) => {
    // Check if it's a Result type (old neverthrow pattern)
    if (event.output && isResult(event.output)) {
      if (event.output.isOk()) return false;
      const error = event.output.error as { code?: string };
      return error.code === "RATE_LIMITED";
    }

    // For new pattern: check event.error
    if (!event.error || typeof event.error !== "object") return false;
    const error = event.error as { code?: string };
    return error.code === "RATE_LIMITED";
  },
  metadata: {
    displayName: "smsRateLimited",
    description: "SMS was rate limited",
    requiresActor: "sendSMS",
    category: "error",
  },
};

/**
 * SMS Verification Success Guard
 */
export const smsVerified: GuardWithMetadata = {
  impl: ({ event }) => {
    // Check if it's a Result type (old neverthrow pattern)
    if (event.output && isResult(event.output)) {
      return event.output.isOk();
    }
    // For new pattern: success means no error
    return !event.error;
  },
  metadata: {
    displayName: "smsVerified",
    description: "SMS code was verified successfully",
    requiresActor: "verifySMS",
    category: "success",
  },
};

/**
 * SMS Verification Failed Guard
 */
export const smsVerificationFailed: GuardWithMetadata = {
  impl: ({ event }) => {
    // Check if it's a Result type (old neverthrow pattern)
    if (event.output && isResult(event.output)) {
      return event.output.isErr();
    }
    // For new pattern: error exists
    return !!event.error;
  },
  metadata: {
    displayName: "smsVerificationFailed",
    description: "SMS code verification failed",
    requiresActor: "verifySMS",
    category: "error",
  },
};

/**
 * SMS Code Expired Guard
 */
export const smsCodeExpired: GuardWithMetadata = {
  impl: ({ event }) => {
    // Check if it's a Result type (old neverthrow pattern)
    if (event.output && isResult(event.output)) {
      if (event.output.isOk()) return false;
      const error = event.output.error as { code?: string };
      return error.code === "EXPIRED";
    }

    // For new pattern: check event.error
    if (!event.error || typeof event.error !== "object") return false;
    const error = event.error as { code?: string };
    return error.code === "EXPIRED";
  },
  metadata: {
    displayName: "smsCodeExpired",
    description: "SMS verification code has expired",
    requiresActor: "verifySMS",
    category: "error",
  },
};

/**
 * OTP Invalid Guard
 * Checks if the OTP code is invalid (wrong code entered)
 */
export const otpInvalid: GuardWithMetadata = {
  impl: ({ event }) => {
    // XState error events have error in event.error
    const error = event.error;
    if (!error || typeof error !== "object") {
      return false;
    }

    const errorCode = (error as { code?: string }).code;
    const isInvalid =
      errorCode === "OTP_INVALID" || errorCode === "INVALID_CODE";
    return isInvalid;
  },
  metadata: {
    displayName: "otpInvalid",
    description: "OTP code is invalid",
    requiresActor: "verifySMS",
    category: "error",
  },
};

/**
 * SMS Returned User ID Guard
 * Checks if the sendSMS actor returned a userId (existing user found)
 */
export const smsReturnedUserId: GuardWithMetadata = {
  impl: ({ event }) => {
    // Check event.output from sendSMS actor
    if (!event.output || typeof event.output !== "object") {
      return false;
    }

    const output = event.output as { userId?: string | null };
    return (
      typeof output.userId === "string" &&
      output.userId !== null &&
      output.userId.length > 0
    );
  },
  metadata: {
    displayName: "smsReturnedUserId",
    description: "sendSMS actor returned a userId (existing user found)",
    requiresActor: "sendSMS",
    category: "success",
  },
};

/**
 * Is SMS Authenticated Guard
 * Checks if user has successfully completed SMS OTP verification
 */
export const isSmsAuthenticated: GuardWithMetadata = {
  impl: ({ context }) => {
    return context.smsAuthenticated === true;
  },
  metadata: {
    displayName: "isSmsAuthenticated",
    description: "User has successfully verified SMS OTP",
    requiresContext: ["smsAuthenticated"],
    category: "context",
  },
};
